import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { GroupsWidget } from "./components/GroupsWidget";
import { CreateCluster } from "./components/CreateCluster";
import { EditCluster } from "./components/EditCluster";
import { useProcessingOverlay } from "@hooks/useProcessingOverlay";
import { fetchClusters, deleteCluster } from "./request/request";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import { useAppSelector } from "@redux/hooks";
import { ClusterCardSkeleton } from "../../utils/ClusterCardSkeleton";

interface ClusterData {
    id: number;
    name: string;
    description: string;
    enable: boolean;
    suscription?: string;
    ctxr: Array<{
        subid: string;
        name: string;
        description: string;
        localpref: number;
        statuspeer: boolean | null;
        lastupdate?: string;
        status: string;
    }>;
}

function HighAvailabilityPage() {
    const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
    const { tenantId } = useAppSelector(state => state.selection);

    const [clusters, setClusters] = useState<ClusterData[]>([]);
    const [isFetching, setIsFetching] = useState(false);

    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [clusterIdToEdit, setClusterIdToEdit] = useState<number | null>(null);

    const [clusterIdToDelete, setClusterIdToDelete] = useState<number | null>(null);
    const [showDeleteAlert, setShowDeleteAlert] = useState(false);
    const [typedConfirm, setTypedConfirm] = useState("");

    const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);
    const [showDeleteError, setShowDeleteError] = useState(false);

    const overlay = useProcessingOverlay(isFetching);

    useEffect(() => {
        if (!isLoading && isAuthenticated) {
            loadClusters();
        }
    }, [isLoading, isAuthenticated, tenantId]);

    async function loadClusters() {
        try {
            setIsFetching(true);
            const token = await getAccessTokenSilently();
            const resp = await fetchClusters(token, tenantId);
            setClusters(resp.data);
        } catch (error) {
            console.error("Error fetching clusters =>", error);
        } finally {
            setIsFetching(false);
        }
    }

    function handleRefresh() {
        loadClusters();
    }

    function handleOpenEdit(clusterId: number) {
        setClusterIdToEdit(clusterId);
        setIsEditOpen(true);
    }

    function handleOpenDelete(clusterId: number) {
        setClusterIdToDelete(clusterId);
        setTypedConfirm("");
        setShowDeleteAlert(true);
    }

    async function handleDeleteConfirm() {
        if (!clusterIdToDelete) return;
        try {
            setIsFetching(true);
            const token = await getAccessTokenSilently();
            const resp = await deleteCluster(token, clusterIdToDelete, tenantId);
            setShowDeleteAlert(false);
            if (resp.data && resp.data.status === "deleted") {
                setShowDeleteSuccess(true);
                loadClusters();
            } else {
                setShowDeleteError(true);
            }
        } catch (error) {
            console.error("Error deleting cluster =>", error);
            setShowDeleteAlert(false);
            setShowDeleteError(true);
        } finally {
            setIsFetching(false);
        }
    }

    function handleDeleteClose() {
        setShowDeleteAlert(false);
        setClusterIdToDelete(null);
        setTypedConfirm("");
    }

    function handleCloseSuccessAlert() {
        setShowDeleteSuccess(false);
    }

    function handleCloseErrorAlert() {
        setShowDeleteError(false);
    }

    let totalDevices = 0;
    let onlineCount = 0;
    let offlineCount = 0;
    let unknownCount = 0;

    clusters.forEach((cl) => {
        totalDevices += cl.ctxr.length;
        cl.ctxr.forEach((device) => {
            if (device.statuspeer === true) onlineCount++;
            else if (device.statuspeer === false) offlineCount++;
            else unknownCount++;
        });
    });

    function calculateOverallStatus(ctxr: ClusterData["ctxr"]) {
        const problematic = ctxr.filter(
            (c) => c.status !== "up" || c.statuspeer !== true
        );
        if (problematic.length === 0) {
            return "All good! The system is running smoothly and everything is in order.";
        } else {
            const messages = problematic
                .map(
                    (p) =>
                        `${p.name}: Internet Status: ${p.status} | HA Status: ${p.statuspeer}`
                )
                .join(" | ");
            return `Some issues detected. Please check individual CTX-R statuses: ${messages}`;
        }
    }

    const disableDeleteButton = typedConfirm.trim().toLowerCase() !== "confirm";

    return (
        <div className="relative">
            <div className="p-4 bg-semantic-bg-primary border border-semantic-border-secondary rounded-sm mb-4">
                <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-semantic-text-primary">
                        Clusters
                    </h2>
                    <div className="flex items-center gap-4 text-sm">
                        <span className="text-semantic-text-primary">
                            Total Devices: {totalDevices}
                        </span>
                        <span className="text-green-500">Online: {onlineCount}</span>
                        <span className="text-red-500">Offline: {offlineCount}</span>
                        <span className="text-semantic-text-primary">
                            Unknown: {unknownCount}
                        </span>
                    </div>
                </div>
                <div className="mt-3 flex justify-end gap-3">
                    <GenericButton
                        label="Refresh"
                        variant="secondary"
                        shape="ghost"
                        size="md"
                        onClick={handleRefresh}
                    />
                    <GenericButton
                        label="Add Cluster"
                        variant="brand"
                        size="md"
                        onClick={() => setIsCreateOpen(true)}
                    />
                </div>
            </div>

            <div className="px-4">
                {isFetching ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                        {[1, 2, 3].map((i) => (
                            <ClusterCardSkeleton key={i} />
                        ))}
                    </div>
                ) : clusters.length === 0 ? (
                    <div className="text-center w-100">
                        <span className="text-red-500 font-bold">No Data Available</span>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                        {clusters.map((c) => (
                            <GroupsWidget
                                key={c.id}
                                name={c.name}
                                ctxs={c.ctxr}
                                id={c.id}
                                enable={c.enable}
                                suscription={c.suscription}
                                overallStatus={calculateOverallStatus(c.ctxr)}
                                setID={handleOpenEdit}
                                onDelete={handleOpenDelete}
                            />
                        ))}
                    </div>
                )}
            </div>

            {isCreateOpen && (
                <CreateCluster
                    isOpen={isCreateOpen}
                    onClose={() => setIsCreateOpen(false)}
                    onSuccess={loadClusters}
                />
            )}

            {isEditOpen && clusterIdToEdit !== null && (
                <EditCluster
                    clusterId={clusterIdToEdit}
                    isOpen={isEditOpen}
                    onClose={() => setIsEditOpen(false)}
                    onSuccess={loadClusters}
                />
            )}

            {showDeleteAlert && (
                <GenericAlert
                    isOpen
                    onClose={handleDeleteClose}
                    onConfirm={handleDeleteConfirm}
                    title="Are you sure?"
                    description={
                        <>
                            <p>
                                Once deleted, you will not be able to recover this Cluster!
                                <br />
                                <small>Note: This will disable HA cluster features, not the instance!</small>
                            </p>
                            <p>
                                Enter <strong>&quot;confirm&quot;</strong> to continue...
                            </p>
                            <input
                                type="text"
                                value={typedConfirm}
                                onChange={(e) => setTypedConfirm(e.target.value)}
                                placeholder="confirm"
                                className="mt-2 w-full border rounded-sm p-2 text-gray-700"
                            />
                        </>
                    }
                    confirmLabel="Yes, delete it!"
                    cancelLabel="Cancel"
                    variant="warning"
                    confirmButtonDisabled={disableDeleteButton}
                />
            )}

            {showDeleteSuccess && (
                <GenericAlert
                    isOpen
                    onClose={handleCloseSuccessAlert}
                    onConfirm={handleCloseSuccessAlert}
                    title="Deleted!"
                    description="Your Cluster has been deleted."
                    variant="success"
                    confirmLabel="OK"
                />
            )}

            {showDeleteError && (
                <GenericAlert
                    isOpen
                    onClose={handleCloseErrorAlert}
                    onConfirm={handleCloseErrorAlert}
                    title="Error"
                    description="Could not delete the Cluster."
                    variant="error"
                    confirmLabel="OK"
                />
            )}

            {overlay}
        </div>
    );
}

export default HighAvailabilityPage;
